import {AnalysisGraphQL, AnalysisViewGraphQL} from 'analysis-tool/AnalysisTypes';
import {getDefaultClientIdForView} from 'analysis-tool/data-view/AnalysisViewUtils';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';

interface ViewGroup {
  views: Array<AnalysisViewGraphQL>;
  viewIds: Set<string>;
  displayName: string;
}

/**
 * Get the groups of layout views that are associated with the same grouping key.
 *
 * Returns a Map, where the key is the grouping key and the value is a ViewGroup object. The ViewGroup object
 * contains the views and view ids of the layout views that should be grouped together in the sidebar.
 * The display name is the title that will be shown on the sidebar.
 */
export default function getLayoutViewGroups(analysis: AnalysisGraphQL): Map<string, ViewGroup> {
  const groupingKeyToLayoutViews: Map<string, ViewGroup> = new Map();

  analysis.views.forEach((view) => {
    const lastStep = view.lineage[view.lineage.length - 1].step;

    if (lastStep.stepType === PipelineStepType.LAYOUT) {
      const layoutConfig = lastStep.config;
      assert(layoutConfig != null, 'Expected non-null layout config');

      const sidebarGrouping = layoutConfig.sidebarGrouping;

      if (sidebarGrouping != null) {
        if (!groupingKeyToLayoutViews.has(sidebarGrouping.key)) {
          groupingKeyToLayoutViews.set(sidebarGrouping.key, {
            views: [],
            viewIds: new Set(),
            displayName: sidebarGrouping.displayName,
          });
        }

        const viewGroup = groupingKeyToLayoutViews.get(sidebarGrouping.key)!;
        const viewId = getDefaultClientIdForView(view);

        viewGroup.views.push(view);
        viewGroup.viewIds.add(viewId);
      }
    }
  });

  return groupingKeyToLayoutViews;
}
